import * as React from 'react'

import classNames from 'classnames/bind'
import isEmpty from 'lodash/isEmpty'
import type moment from 'moment'

import { observer } from 'mobx-react'

import createComputed from 'modules/core/hooks/createComputed'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { matchText } from 'modules/core/lib/stringUtils'
import { employeeUser } from 'modules/core/store/collections/users'

import type Employee from 'modules/employees/store/models/Employee'

import Store from '../Store'
import TableBody from '../TableBody'
import TableHeader from '../TableHeader'
import styles from './index.module.scss'

const cx = classNames.bind(styles)

const useSearchFilter = createComputed((search: string, employees: Employee[]) => {
  return employees
    .filter((employee) => isEmpty(search) || matchText(search, employeeUser(employee).fullName()))
    .sort((a, b) => {
      return employeeUser(a).fullName().localeCompare(employeeUser(b).fullName())
    })
})
interface Props {
  date: moment.Moment
  filteredEmployees: Employee[]
  changingData?: boolean
  employeesWorkingDays: Record<string, string>
}

const TeamViewTable = ({
  date,
  changingData = false,
  filteredEmployees,
  employeesWorkingDays,
}: Props) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const { access } = useCurrentSession()

  const store = React.useMemo(
    () => new Store(date, filteredEmployees, access),
    [date, filteredEmployees, changingData, access]
  )

  const [scrollWrapper, setScrollWrapper] = React.useState<HTMLElement | null>(null)

  const setScrollWrapperRef = React.useCallback(
    (node: React.SetStateAction<HTMLElement | null>) => node && setScrollWrapper(node),
    []
  )

  const employeesToShow = useSearchFilter(debouncedSearch, filteredEmployees).get()

  return (
    <div
      className={cx('wrapper', {
        changingData,
      })}
      style={{
        height: '100%',
        width: '100%',
      }}
      ref={setScrollWrapperRef}
    >
      <table className={styles.table}>
        <TableHeader store={store} search={search} onSearch={onSearch} />
        <TableBody
          store={store}
          employees={employeesToShow}
          scrollWrapper={scrollWrapper}
          employeesWorkingDays={employeesWorkingDays}
        />
      </table>
    </div>
  )
}

export default observer(TeamViewTable)
